import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from "react";
import clsx from 'clsx';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import { useRef, useState, useMemo } from 'react';
import { useFirestore } from 'reactfire';
import { useMeasure } from 'react-use';
import { useClickAway, useIntersection } from 'react-use';
import { nonUserFacingTags } from '@/config/templates';
import Button from '@/components/Button';
import ButtonList from '@/components/ButtonList';
import useSharedWidgets from '@/hooks/useSharedWidgets';
import { toStylesAttribute } from '@/utils/dom';
import CheckList, { Item as CheckListItem } from '@/components/FormElements/CheckList';
import responsiveUtils from '@/utils/responsive';
import { titleize, dedasherize } from '@/utils/strings';
import ConfirmModal from './ConfirmModal';
import PreviewModal from './PreviewModal';
import Loading, { ImageLoader } from './Loading';
import TagList from './TagList';
import SpreadsheetLink from './SpreadsheetLink';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var LightningIcon = function LightningIcon(props) {
  return /*#__PURE__*/_jsx("svg", _objectSpread(_objectSpread({}, props), {}, {
    children: /*#__PURE__*/_jsx("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M8.22504 11L7.72867 15.6463L12.9468 9.00005H7.77469L8.27028 4.36618L3.06264 11H8.22504ZM10.6295 1.11438C10.7363 0.115589 9.4688 -0.399562 8.84855 0.390555L0.219728 11.3826C-0.29539 12.0388 0.172086 13 1.00631 13H5.99999L5.36999 18.8972C5.26329 19.8959 6.53063 20.4109 7.15087 19.6209L15.7898 8.61758C16.305 7.9614 15.8376 7.00005 15.0033 7.00005H9.99999L10.6295 1.11438Z",
      fill: "#12131A"
    })
  }));
};

LightningIcon.defaultProps = {
  width: "17",
  height: "21",
  viewBox: "0 0 17 21",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
};

const SharedWidgetSelect = _ref => {
  let {
    className,
    omitTags,
    searchTags,
    filterTags,
    onPreview = widget => widget,
    onSelect = widget => widget
  } = _ref;
  const containerEl = useRef(null);
  const [setupUseMeasure, {
    width
  }] = useMeasure();
  const {
    isIntersecting: isVisible
  } = useIntersection(containerEl, {}) || {
    isIntersecting: false
  };
  const isMedium = useMemo(() => responsiveUtils.isMedium(width), [width]);
  const isLarge = useMemo(() => responsiveUtils.isLarge(width), [width]);
  return /*#__PURE__*/_jsx("div", {
    ref: el => {
      /* @ts-ignore */
      setupUseMeasure(el);
      /* @ts-ignore */

      containerEl.current = el;
    },
    className: clsx(styles.container, isLarge ? styles['container--grid'] : '', className),
    children: isVisible ? /*#__PURE__*/_jsx(Content, {
      isMedium: isMedium,
      isLarge: isLarge,
      omitTags: omitTags,
      searchTags: searchTags,
      filterTags: filterTags,
      onPreview: onPreview,
      onSelect: onSelect
    }) : /*#__PURE__*/_jsx(Loading, {
      isMedium: isMedium,
      isLarge: isLarge
    })
  });
};

const Content = _ref2 => {
  let {
    isMedium,
    isLarge,
    omitTags,
    searchTags,
    filterTags,
    onPreview,
    onSelect
  } = _ref2;
  const firestore = useFirestore();
  const {
    status,
    data: sharedWidgets,
    error
  } = useSharedWidgets(firestore, undefined, searchTags);
  const hasError = Boolean(error);
  const isLoading = status === 'loading';
  const {
    0: activeWidget,
    1: setActiveWidget
  } = useState(null); // Filter out widgets that have tags in the omitTags array

  const widgets = useMemo(() => sharedWidgets.filter(widget => {
    return widget.tags && !widget.tags.some(tag => (omitTags || []).includes(tag));
  }), [omitTags, sharedWidgets]);
  const filters = useMemo(() => {
    // Get all unique tags
    const allTags = flatten(widgets.map(widget => widget.tags || []));
    const uniqueTags = uniq(allTags); // Create tag frequency map

    const tagCounts = {};
    widgets.forEach(widget => {
      (widget.tags || []).forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    }); // Build filter objects from visible tags

    return uniqueTags.filter(tag => !nonUserFacingTags.includes(tag) || (filterTags === null || filterTags === void 0 ? void 0 : filterTags.includes(tag))).map(name => ({
      name,
      counter: tagCounts[name] || 0
    }));
  }, [widgets, filterTags]);
  const {
    0: activeFilters,
    1: setActiveFilters
  } = useState([]);
  const renderedWidgets = (activeFilters.length ? widgets.filter(widget => (widget.tags || []).some(tag => activeFilters.includes(tag))) : widgets).sort((a, b) => `${a.title || ''}`.localeCompare(b.title || '', 'en'));

  const onFilterChange = filterName => {
    const filtersUpdate = [...activeFilters];
    const isActive = filtersUpdate.includes(filterName);
    isActive ? filtersUpdate.splice(filtersUpdate.indexOf(filterName), 1) : filtersUpdate.push(filterName);
    setActiveFilters(filtersUpdate);
  };

  const onClearFilter = () => {
    setActiveFilters([]);
  };

  const onBlurActiveWidget = widget => {
    if (widget === activeWidget) {
      setActiveWidget(null);
    }
  };

  if (isLoading) {
    return /*#__PURE__*/_jsx(Loading, {
      isMedium: isMedium,
      isLarge: isLarge
    });
  } else if (hasError) {
    return /*#__PURE__*/_jsx("span", {
      children: "Error, please reload page."
    });
  } else if (!widgets.length) {
    return /*#__PURE__*/_jsx("span", {
      children: "No widgets available."
    });
  } else {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("aside", {
        className: styles.sidebar,
        children: [!isLarge ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("h6", {
            className: '-mb-xsmall',
            children: "Filters:"
          }), /*#__PURE__*/_jsxs(ButtonList, {
            variant: ['inline', 'flat'],
            className: styles.buttonList,
            children: [/*#__PURE__*/_jsx(Button, {
              size: "small",
              variant: activeFilters.length === 0 ? 'primary' : 'secondary',
              disabled: activeFilters.length === 0,
              onClick: onClearFilter,
              children: "All"
            }), filters.map(filter => /*#__PURE__*/_jsxs(Button, {
              size: "small",
              nonActive: true,
              variant: activeFilters.includes(filter.name) ? 'primary' : 'secondary',
              onClick: () => onFilterChange(filter.name),
              children: [titleize(dedasherize(filter.name)), " (", filter.counter, ")"]
            }, `button-${filter.name}`))]
          })]
        }) : /*#__PURE__*/_jsx(_Fragment, {}), isLarge ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("h6", {
            children: "Filters"
          }), /*#__PURE__*/_jsx(CheckList, {
            className: styles.filters,
            children: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsxs(CheckListItem, {
                value: "all",
                checked: activeFilters.length === 0,
                disabled: activeFilters.length === 0,
                className: styles.filters__row,
                onChange: onClearFilter,
                name: "shared-widget-filter",
                children: [/*#__PURE__*/_jsx("span", {
                  children: "All"
                }), /*#__PURE__*/_jsxs("span", {
                  children: [widgets.length, " widget", widgets.length > 1 ? 's' : '']
                })]
              }), filters.map(filter => /*#__PURE__*/_jsxs(CheckListItem, {
                value: filter.name,
                checked: activeFilters.includes(filter.name),
                className: styles.filters__row,
                onChange: () => onFilterChange(filter.name),
                name: "shared-widget-filter",
                children: [/*#__PURE__*/_jsx("span", {
                  children: titleize(dedasherize(filter.name))
                }), /*#__PURE__*/_jsxs("span", {
                  children: [filter.counter, " widget", filter.counter > 1 ? 's' : '']
                })]
              }, `checkbox-${filter.name}`))]
            })
          })]
        }) : /*#__PURE__*/_jsx(_Fragment, {})]
      }), /*#__PURE__*/_jsx("ul", {
        className: clsx(styles.list, isMedium || isLarge ? styles['list--grid'] : ''),
        children: renderedWidgets.map(widget => {
          var _widget$tags, _widget$tags2;

          return /*#__PURE__*/_jsxs(ContentItem, {
            className: activeWidget === widget ? styles['item--active'] : '',
            onClick: () => setActiveWidget(widget),
            onBlur: () => onBlurActiveWidget(widget),
            children: [/*#__PURE__*/_jsxs("div", {
              className: styles.itemOverlay,
              children: [/*#__PURE__*/_jsx("h6", {
                className: styles.activeHeading,
                children: widget.title
              }), /*#__PURE__*/_jsx("p", {
                className: clsx(styles.itemDescription, '-mb'),
                children: widget.description
              }), /*#__PURE__*/_jsxs("div", {
                className: styles.itemOverlay__meta,
                children: [/*#__PURE__*/_jsx(TagList, {
                  keyPrefix: `${widget.id}-shared`,
                  tags: (_widget$tags = widget.tags) === null || _widget$tags === void 0 ? void 0 : _widget$tags.filter(tag => !nonUserFacingTags.includes(tag)),
                  variant: "hashtag"
                }), widget.spreadsheetSrc ? /*#__PURE__*/_jsxs(_Fragment, {
                  children: [/*#__PURE__*/_jsx("span", {
                    children: "\xA0//\xA0"
                  }), /*#__PURE__*/_jsx(SpreadsheetLink, {
                    href: widget.spreadsheetSrc
                  })]
                }) : /*#__PURE__*/_jsx(_Fragment, {})]
              }), /*#__PURE__*/_jsxs("footer", {
                className: styles.itemOverlay__footer,
                children: [/*#__PURE__*/_jsx(Button, {
                  variant: "secondary",
                  leftIcon: /*#__PURE__*/_jsx(LightningIcon, {}),
                  onClick: () => onPreview(widget),
                  children: "Preview"
                }), /*#__PURE__*/_jsx(Button, {
                  onClick: () => onSelect(widget),
                  children: "Use Template"
                })]
              })]
            }), /*#__PURE__*/_jsx("aside", {
              className: styles.item__preview,
              children: /*#__PURE__*/_jsx(ContentItemImage, {
                widget: widget
              })
            }), /*#__PURE__*/_jsxs("footer", {
              className: styles.item__footer,
              children: [/*#__PURE__*/_jsx("h6", {
                className: styles.itemHeading,
                children: widget.title
              }), /*#__PURE__*/_jsx(TagList, {
                keyPrefix: `${widget.id}-shared`,
                tags: (_widget$tags2 = widget.tags) === null || _widget$tags2 === void 0 ? void 0 : _widget$tags2.filter(tag => !nonUserFacingTags.includes(tag)),
                variant: "pill"
              })]
            })]
          }, `shared-widgets-${widget.id}`);
        })
      })]
    });
  }
};

const ContentItem = _ref3 => {
  let {
    className,
    children,
    onClick,
    onBlur
  } = _ref3;
  const ref = useRef(null);
  useClickAway(ref, onBlur);
  return /*#__PURE__*/_jsx("li", {
    ref: ref,
    className: clsx(styles.item, className || ''),
    onClick: onClick,
    children: children
  });
}; // Preview Image & Loader


const ContentItemImage = _ref4 => {
  let {
    widget
  } = _ref4;
  const {
    0: isLoaded,
    1: setIsLoaded
  } = useState(false);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [!isLoaded ? /*#__PURE__*/_jsx(ImageLoader, {}) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx("img", {
      src: `/sharedWidgets/${widget.sharedNamespace}/primary.webp`,
      alt: `${widget.title} - widget preview image`,
      style: toStylesAttribute({
        display: isLoaded ? null : 'none'
      }),
      onLoad: handleLoad,
      width: 1095,
      height: 657
    })]
  });
};

export default SharedWidgetSelect;
export { ConfirmModal, PreviewModal };